import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSearchField,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowAvatar,
  AdminRowActionsButton,
  AdminRowMenuItem,
  AdminSupportingText,
  AdminListFilterDropdown,
  AdminRowMenuIcon,
  ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import useClickOutside from '../../../Hooks/useClickOutside';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import ProfilePlaceholder from '../../../assets/img/User.png';

const DEFAULT_FILTERS = {
  type: 'All',
  category: 'All',
  supportTeam: 'All',
};

/** Case ID · Business · Type · Name · Trade Category · Decision · Support Team · Finalised On · Actions */
const ARCHIVE_TABLE_GRID =
  'minmax(0, 1.05fr) minmax(0, 1.2fr) minmax(0, 0.65fr) minmax(0, 1.15fr) minmax(0, 0.85fr) minmax(0, 0.85fr) minmax(0, 1fr) minmax(0, 0.95fr) minmax(62px, auto)';

const COMPLIANCE_TYPES = ['Licence', 'Badge', 'Ticket'];
const CASE_TYPES = ['New', 'Renewal', 'Replacement'];
const TEAM_MEMBER_NAMES = ['Jordan Lee', 'Mia Chen', 'Olivia Smith', 'Daniel Price'];
const TRADE_CATEGORIES = [
  'Plumbing',
  'Electrical',
  'Building',
  'Gas Fitting',
  'Carpentry',
  'Roofing',
  'Painting',
  'Landscaping',
];
const CATEGORY_FILTER_OPTIONS = ['All Trades', ...TRADE_CATEGORIES];
const APPLIED_RULE_BY_SOURCE_ID = {
  'LIC-0002': 'COM-0001',
  'LIC-0001': 'COM-0002',
  'BDG-0001': 'COM-0003',
  'TKT-0001': 'COM-0004',
};

const COMPLIANCE_ITEMS_BY_TYPE = {
  Licence: [
    { id: 'LIC-0001', name: 'Electrical Licence', tradeCategory: 'Electrical' },
    { id: 'LIC-0002', name: 'Plumbing Licence', tradeCategory: 'Plumbing' },
    { id: 'LIC-0003', name: 'Gas Fitting Licence', tradeCategory: 'Plumbing' },
  ],
  Badge: [
    { id: 'BDG-0001', name: 'First Aid', tradeCategory: 'All Trades' },
    { id: 'BDG-0002', name: 'Industry Membership', tradeCategory: 'All Trades' },
    { id: 'BDG-0003', name: 'Working with Children Check', tradeCategory: 'All Trades' },
  ],
  Ticket: [
    { id: 'TKT-0001', name: 'White Card', tradeCategory: 'All Trades' },
    { id: 'TKT-0002', name: 'Working at Heights', tradeCategory: 'All Trades' },
    { id: 'TKT-0003', name: 'Confined Spaces', tradeCategory: 'All Trades' },
  ],
};

const parseSampleDate = (day, monthShort, year, hour, minute, isPm) => {
  const months = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
  };
  let h = hour;
  if (isPm && h < 12) h += 12;
  if (!isPm && h === 12) h = 0;
  return new Date(year, months[monthShort], day, h, minute, 0, 0).toISOString();
};

const decisionIdFromCaseId = (caseId) => {
  const digits = String(caseId || '').replace(/\D/g, '');
  return `CMP-DEC-${(digits || '0000').slice(-4)}`;
};

const buildDummyArchiveItems = () => {
  const fixedItems = [
    {
      complianceCaseId: 'CMP-004180',
      businessName: 'Apex Plumbing',
      businessId: 'BUS-001284',
      businessAvatar: 'https://i.pravatar.cc/150?img=12',
      complianceType: 'Licence',
      complianceItem: 'Plumbing Licence',
      complianceItemId: 'LIC-0002',
      appliedRuleId: 'COM-0001',
      tradeCategory: 'Plumbing',
      businessTradeCategory: 'Plumbing',
      caseType: 'Renewal',
      supportTeamMember: 'Jordan Lee',
      finalOutcome: 'Approved',
      recordStatus: 'Active',
      decisionId: 'CMP-DEC-4180',
      finalisedOn: parseSampleDate(15, 'Mar', 2026, 2, 20, true),
      submittedAt: parseSampleDate(3, 'Mar', 2026, 9, 0, false),
    },
    {
      complianceCaseId: 'CMP-004181',
      businessName: 'Bright Spark Electrical',
      businessId: 'BUS-001285',
      businessAvatar: 'https://i.pravatar.cc/150?img=33',
      complianceType: 'Ticket',
      complianceItem: 'White Card',
      complianceItemId: 'TKT-0001',
      appliedRuleId: 'COM-0004',
      tradeCategory: 'All Trades',
      businessTradeCategory: 'Electrical',
      caseType: 'New',
      supportTeamMember: 'Mia Chen',
      finalOutcome: 'Rejected',
      recordStatus: 'No Record',
      decisionId: 'CMP-DEC-4181',
      finalisedOn: parseSampleDate(18, 'Mar', 2026, 12, 0, false),
      submittedAt: parseSampleDate(10, 'Mar', 2026, 9, 18, false),
    },
    {
      complianceCaseId: 'CMP-004182',
      businessName: 'Metro Building Group',
      businessId: 'BUS-001287',
      businessAvatar: 'https://i.pravatar.cc/150?img=44',
      complianceType: 'Licence',
      complianceItem: 'Electrical Licence',
      complianceItemId: 'LIC-0001',
      appliedRuleId: 'COM-0002',
      tradeCategory: 'Electrical',
      businessTradeCategory: 'Building',
      caseType: 'Replacement',
      supportTeamMember: 'Jordan Lee',
      finalOutcome: 'Approved',
      recordStatus: 'Active',
      decisionId: 'CMP-DEC-4182',
      finalisedOn: parseSampleDate(20, 'Mar', 2026, 4, 30, true),
      submittedAt: parseSampleDate(12, 'Mar', 2026, 2, 35, true),
    },
    {
      complianceCaseId: 'CMP-003983',
      businessName: 'Urban Gas Works',
      businessId: 'BUS-001286',
      businessAvatar: 'https://i.pravatar.cc/150?img=22',
      complianceType: 'Badge',
      complianceItem: 'First Aid',
      complianceItemId: 'BDG-0001',
      appliedRuleId: 'COM-0003',
      tradeCategory: 'All Trades',
      businessTradeCategory: 'Plumbing',
      caseType: 'New',
      supportTeamMember: 'Jordan Lee',
      finalOutcome: 'Approved',
      recordStatus: 'Active',
      decisionId: 'CMP-DEC-3983',
      finalisedOn: parseSampleDate(2, 'Apr', 2025, 10, 24, false),
      submittedAt: parseSampleDate(28, 'Mar', 2025, 9, 0, false),
    },
  ];

  const businessNames = [
    'Northside Plumbing Co.',
    'Bolt Electrical Services',
    'Coastal Gas & Heating',
    'Modern Painters Collective',
    'Master Builders Guild',
    'Green Lawn Care Pty Ltd',
    'Smart Locksmith Co.',
    'Elite Flooring Professionals',
    'QuickFix Tiling & Reno',
    'Rapid Roofing Group',
  ];

  const extra = [];
  for (let i = 0; i < 24; i++) {
    const n = 3900 + i;
    const complianceType = COMPLIANCE_TYPES[i % COMPLIANCE_TYPES.length];
    const caseType = CASE_TYPES[i % CASE_TYPES.length];
    const items = COMPLIANCE_ITEMS_BY_TYPE[complianceType];
    const item = items[i % items.length];
    const businessTradeCategory = TRADE_CATEGORIES[i % TRADE_CATEGORIES.length];
    const rejected = i % 3 === 0;
    const keepCurrent = rejected && caseType !== 'New';
    extra.push({
      complianceCaseId: `CMP-${String(n).padStart(6, '0')}`,
      businessName: businessNames[i % businessNames.length],
      businessId: `BUS-${String(300 + i).padStart(6, '0')}`,
      businessAvatar: `https://i.pravatar.cc/150?img=${(i % 70) + 5}`,
      complianceType,
      complianceItem: item.name,
      complianceItemId: item.id,
      appliedRuleId: APPLIED_RULE_BY_SOURCE_ID[item.id] || null,
      tradeCategory: item.tradeCategory || businessTradeCategory,
      businessTradeCategory,
      caseType,
      supportTeamMember: TEAM_MEMBER_NAMES[i % TEAM_MEMBER_NAMES.length],
      finalOutcome: rejected ? 'Rejected' : 'Approved',
      recordStatus: rejected && !keepCurrent ? 'No Record' : 'Active',
      decisionId: decisionIdFromCaseId(`CMP-${String(n).padStart(6, '0')}`),
      finalisedOn: parseSampleDate(4 + (i % 20), 'Mar', 2026, 9 + (i % 8), (i * 7) % 60, i % 2 === 0),
      submittedAt: parseSampleDate(1 + (i % 12), 'Mar', 2026, 8 + (i % 6), (i * 11) % 60, false),
    });
  }

  return [...fixedItems, ...extra].map((itemData) => ({
    id: itemData.complianceCaseId,
    ...itemData,
    finalDecision: itemData.finalOutcome,
  }));
};

const formatFinalisedOnDisplay = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const datePart = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart}, ${timePart}`;
};

const ComplianceArchive = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [items] = useState(buildDummyArchiveItems);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [currentPage, setCurrentPage] = useState(1);

  const typeRef = useRef(null);
  const categoryRef = useRef(null);
  const supportTeamRef = useRef(null);
  const [typeOpen, setTypeOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [supportTeamOpen, setSupportTeamOpen] = useState(false);

  useClickOutside(typeRef, () => setTypeOpen(false));
  useClickOutside(categoryRef, () => setCategoryOpen(false));
  useClickOutside(supportTeamRef, () => setSupportTeamOpen(false));

  const [archiveListRowActionsOpen, setArchiveListRowActionsOpen] = useState({});
  const archiveListRowActionsRefs = useRef({});
  const tableScrollRef = useRef(null);

  const toggleArchiveListRowActions = (rowKey) => {
    setArchiveListRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(archiveListRowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setArchiveListRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters]);

  const filteredItems = useMemo(() => {
    const lowerSearch = debouncedSearch.toLowerCase();
    return items.filter((item) => {
      const matchesSearch =
        !lowerSearch ||
        [
          item.complianceCaseId,
          item.businessId,
          item.businessName,
          item.complianceItemId,
          item.complianceItem,
          item.complianceType,
          item.tradeCategory,
          item.supportTeamMember,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(lowerSearch);

      const matchesType = filters.type === 'All' || item.complianceType === filters.type;
      const matchesCategory =
        filters.category === 'All' || item.tradeCategory === filters.category;
      const matchesSupportTeam =
        filters.supportTeam === 'All' ||
        (item.supportTeamMember || 'Unassigned') === filters.supportTeam;

      return matchesSearch && matchesType && matchesCategory && matchesSupportTeam;
    });
  }, [items, debouncedSearch, filters]);

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      const ta = a.finalisedOn ? new Date(a.finalisedOn).getTime() : 0;
      const tb = b.finalisedOn ? new Date(b.finalisedOn).getTime() : 0;
      return tb - ta;
    });
  }, [filteredItems]);

  const totalCount = sortedItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, Math.max(1, Math.ceil(sortedItems.length / CONFIG_LIST_PAGE_SIZE))));
  }, [sortedItems.length]);

  const paginatedItems = useMemo(() => {
    const start = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
    return sortedItems.slice(start, start + CONFIG_LIST_PAGE_SIZE);
  }, [sortedItems, effectivePage]);

  const goToRecord = (item) => {
    navigate(`/admin/verification/archive/${item.id}`, {
      state: {
        archiveItem: {
          ...item,
          complianceId: item.complianceCaseId,
          documentName: item.complianceItem,
          category: item.complianceType,
          finalStatus: item.finalOutcome,
          finalDecision: item.finalOutcome,
        },
        from: location.pathname,
      },
    });
  };

  const rangeStart = totalCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount);
  const showPagination = totalCount > CONFIG_LIST_PAGE_SIZE;

  const typeFilterOptions = useMemo(
    () => [
      { value: 'All', label: 'All' },
      ...COMPLIANCE_TYPES.map((option) => ({ value: option, label: option })),
    ],
    []
  );
  const categoryFilterOptions = useMemo(
    () => [
      { value: 'All', label: 'All' },
      ...CATEGORY_FILTER_OPTIONS.map((option) => ({ value: option, label: option })),
    ],
    []
  );
  const supportTeamFilterOptions = useMemo(() => {
    const names = [
      ...new Set(
        items
          .map((item) => item.supportTeamMember)
          .filter((name) => name && name !== 'Unassigned' && name !== '—')
      ),
    ].sort((a, b) => a.localeCompare(b));
    return [
      { value: 'All', label: 'All' },
      { value: 'Unassigned', label: 'Unassigned' },
      ...names.map((name) => ({ value: name, label: name })),
    ];
  }, [items]);

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Compliance Archive</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Search archived Compliance cases"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search archived Compliance cases"
            />
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Type"
                value={filters.type}
                options={typeFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, type: value }))}
                menuRef={typeRef}
                open={typeOpen}
                setOpen={setTypeOpen}
                size="lg"
              />
              <AdminListFilterDropdown
                label="Category"
                value={filters.category}
                options={categoryFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, category: value }))}
                menuRef={categoryRef}
                open={categoryOpen}
                setOpen={setCategoryOpen}
                size="lg"
              />
              <AdminListFilterDropdown
                label="Support Team"
                value={filters.supportTeam}
                options={supportTeamFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, supportTeam: value }))}
                menuRef={supportTeamRef}
                open={supportTeamOpen}
                setOpen={setSupportTeamOpen}
                size="lg"
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div className="min-w-0">
              <div
                className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2"
                style={{ gridTemplateColumns: ARCHIVE_TABLE_GRID }}
              >
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Case ID</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Business</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Type</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Name</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Trade Category</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Decision</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Support Team</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Finalised On</AdminTableHeader>
                </div>
                <div className="flex items-center justify-center min-w-0">
                  <AdminTableHeader>Actions</AdminTableHeader>
                </div>
              </div>

              {paginatedItems.length === 0 ? (
                <div className="py-10 px-4 text-center">
                  <AdminSupportingText className="text-mediumgray">
                    No archive records found matching your filters.
                  </AdminSupportingText>
                </div>
              ) : (
                paginatedItems.map((item) => {
                  const archiveRowKey = item.complianceCaseId || item.id;
                  return (
                    <div
                      key={item.id}
                      className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 cursor-pointer hover:bg-pastalBlueWhite/50"
                      style={{ gridTemplateColumns: ARCHIVE_TABLE_GRID }}
                      onClick={() => goToRecord(item)}
                    >
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.complianceCaseId}>
                          {item.complianceCaseId}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <AdminRowAvatar
                          src={item.businessAvatar || ProfilePlaceholder}
                          alt={item.businessName || ''}
                          onError={(e) => {
                            e.target.src = ProfilePlaceholder;
                          }}
                        />
                        <AdminTableBody emphasis className="truncate" title={item.businessName}>
                          {item.businessName}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.complianceType}>
                          {item.complianceType}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.complianceItem}>
                          {item.complianceItem}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.tradeCategory}>
                          {item.tradeCategory}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminStatusBadge status={item.finalOutcome} label={item.finalOutcome} />
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody
                          className="truncate"
                          title={item.supportTeamMember || 'Unassigned'}
                        >
                          {item.supportTeamMember || 'Unassigned'}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableMeta className="truncate" title={formatFinalisedOnDisplay(item.finalisedOn)}>
                          {formatFinalisedOnDisplay(item.finalisedOn)}
                        </AdminTableMeta>
                      </div>
                      <div
                        className="flex items-center justify-center relative"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            archiveListRowActionsRefs.current[archiveRowKey] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleArchiveListRowActions(archiveRowKey);
                            }}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {archiveListRowActionsOpen[archiveRowKey] ? (
                            <div className={ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS}>
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  goToRecord(item);
                                  setArchiveListRowActionsOpen({});
                                }}
                              >
                                Open Case
                              </AdminRowMenuItem>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default ComplianceArchive;
